/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/*
AdobePatentID="P8158-US"
*/
import https from 'https';
import getConfig from '../../config';
import {SET_PROFILE, SET_PROFILE_TIMEOUT_OR_ERROR, SET_PROFILE_FETCH_FINISHED, SET_RAW_PROFILE} from '../types';
import ProfileStorage from '../../src/personalization/ProfileStorage';
import uuid from 'uuid';

declare let adobeIMS: AdobeIMS;

// const UNKNOWN_USER_NAME_IDENTIFIER: string = '_unknown_';

const defaultStorage: ProfileStorage = {
	total: 0,
	used: 0,
	available: 0,
};
const defaultSubs: gqlSubscriptions = {
	free: [
		{
			planCode: 'cc_free',
		},
	],
	paid: [],
};

const emptyGqlProfile: gqlProfile = {
	account: {
		stakeholder: false,
		storage: defaultStorage,
		subscriptions: defaultSubs,
	},
	currentTimestamp: new Date().toISOString(),
};

const emptyProfile = {
	currentTimestamp: new Date().toISOString(),
	subscription: {
		planCode: 'cc_free',
	},
	stakeholder: false,
	entitlements: [],
	storage: {
		storage_quota: 0,
		storage_used: 0,
		storage_available: 0,
	},
};

class ProfileService {
	_profile: any; // using any type because we are deprecating this soon
	_rawProfile: gqlProfile | null | undefined;
	// $FlowFixMe
	_store;

	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */
	static instance: ProfileService;

	// $FlowFixMe
	constructor(store) {
		if (!ProfileService.instance) {
			const cachedProfile = ProfileStorage.readProfilesResponseFromSessionStorage();
			const cachedGqlProfile = ProfileStorage.readGqlProfilesResponseFromSessionStorage();
			if (cachedProfile) {
				this._profile = cachedProfile;
			}
			else {
				this._profile = undefined;
			}

			if (cachedGqlProfile) {
				this._rawProfile = cachedGqlProfile;
			}
			else {
				this._rawProfile = undefined;
			}

			this._store = store;
			if (this._rawProfile) {
				window.cchome.util.logger.log('Profile Service', 'Setting raw cached profile', this._rawProfile);
				this._dispatchSetGqlProfile();
			}
			if (this._profile) {
				window.cchome.util.logger.log('Profile Service', 'Setting cached profile', this._profile);
				this._dispatchSetProfile();
			}

			ProfileService.instance = this;
		}
		return ProfileService.instance;
	}

	/**
	 * Dispatches an action to update the profile store to indicate that the profile fetch has errored or timed out.
	 */
	_dispatchProfileFetchError() {
		// Update the profile store to report that the fetch has errored out.
		this._store.dispatch({
			type: SET_PROFILE_TIMEOUT_OR_ERROR,
			payload: true,
		});
	}

	/**
	 * Dispatches an action to update the profile store to indicate that the profile fetch has completed (with failure or success)
	 */
	_dispatchProfileFetchCompleted() {
		// Update the profile store to report that the fetch has completed.
		this._store.dispatch({
			type: SET_PROFILE_FETCH_FINISHED,
			payload: true,
		});
	}

	/**
	 * Dispatches an action to update the profile store with the updated profile data
	 */
	_dispatchSetProfile() {
		// Update the profile store to report that the fetch has completed.
		this._store.dispatch({
			type: SET_PROFILE,
			payload: this._profile,
		});
	}

	/**
	 * Dispatches an action to update the profile store with the updated raw profile data from gql
	 */
	_dispatchSetGqlProfile() {
		// Update the profile store to report that the fetch has completed.
		this._store.dispatch({
			type: SET_RAW_PROFILE,
			payload: this._rawProfile,
		});
	}

	_validateProfile(data: ProfileData) {
		// any field can be null from graphql, so we need to check for everything
		const errors: ProfileErrors = {
			subscriptionErrorFree: false,
			subscriptionErrorPaid: false,
			subscriptionError: false,
			storageError: false,
			stakeholderError: false,
			currentTimestampError: false,
			accountError: false,
		};

		if (!data.account) {
			errors.accountError = true;
		}

		if (!data.currentTimestamp) {
			errors.currentTimestampError = true;
		}

		if (data.account) {
			const {account} = data;
			const {stakeholder, storage, subscriptions} = account;

			if (stakeholder === null) {
				errors.stakeholderError = true;
			}
			if (storage === null) {
				errors.storageError = true;
			}

			if (storage) {
				if (storage.available === null || storage.total === null || storage.used === null) {
					errors.storageError = true;
				}
			}

			if (subscriptions === null) {
				errors.subscriptionError = true;
			}

			if (subscriptions) {
				if (subscriptions.free === null) {
					errors.subscriptionErrorFree = true;
				}
				if (subscriptions.paid === null) {
					errors.subscriptionErrorPaid = true;
				}
				else {
					if (subscriptions.paid.length > 0) {
						// Check the properties of the plans.
						for (const paidSub of subscriptions.paid) {
							if (
								paidSub.planCode === null
								|| paidSub.customerSegment === null
								|| paidSub.planName === null
								|| paidSub.entitlements === null
							) {
								errors.subscriptionErrorPaid = true;
								break;
							}
						}
					}
				}
			}

			if (
				!errors.subscriptionErrorFree
				&& !errors.subscriptionErrorPaid
				&& !subscriptions.free.length
				&& !subscriptions.paid.length
			) {
				// if both free and paid arrays are empty
				errors.subscriptionError = true;
			}
		}

		return errors;
	}

	/**
	 * Pre-format the graphql response based on the errors we get (if any) so that we can use default profile data if there are any missing part
	 */
	_preFormatData(data: Record<string, unknown>, errors: ProfileErrors) {
		const preFormattedData = JSON.parse(JSON.stringify(data));

		if (errors.accountError) {
			preFormattedData.account = {
				subscriptions: defaultSubs,
				stakeholder: false,
				storage: defaultStorage,
			};
		}
		else {
			if (errors.storageError) {
				preFormattedData.account.storage = defaultStorage;
			}
			if (errors.subscriptionError) {
				preFormattedData.account.subscriptions = defaultSubs;
			}
			if (errors.subscriptionErrorFree) {
				preFormattedData.account.subscriptions.free = defaultSubs.free;
			}
			if (errors.subscriptionErrorPaid) {
				preFormattedData.account.subscriptions.paid = [];

				if (preFormattedData.account.subscriptions.free.length === 0) {
					preFormattedData.account.subscriptions.free = defaultSubs.free;
				}
			}
			if (errors.stakeholderError) {
				preFormattedData.account.stakeholder = false;
			}
		}

		if (errors.currentTimestampError) {
			preFormattedData.currentTimestamp = new Date().toISOString();
		}
		return preFormattedData;
	}

	// TODO: this is only temporary, for backward compatibility purpose.
	_formatProfileData(dataObject: Record<string, unknown>) {
		const data = JSON.parse(JSON.stringify(dataObject));

		const freeSub = data.account.subscriptions.free[0];
		const paidSub = data.account.subscriptions.paid[0];

		/* eslint-disable camelcase */
		let userProfile = {
			subscription: paidSub ? paidSub : freeSub,
			storage: {
				storage_quota: data.account.storage.total,
				storage_used: data.account.storage.used,
				storage_available: data.account.storage.available,
			},
			stakeholder: data.account.stakeholder,
			currentTimestamp: data.currentTimestamp,
		};

		/* eslint-enable camelcase */
		let status = 'base';

		if (paidSub) {
			let allTrial = true;
			let promotionType = 'none';

			data.account.subscriptions.paid.forEach((sub) => {
				allTrial = allTrial && sub.offerType === 'trial';
				if (sub.promotionType && promotionType === 'none') {
					promotionType = sub.promotionType;
				}
			});
			if (allTrial) {
				status = 'trial';
			}

			if (userProfile.subscription) {
				userProfile.subscription.status = status;
				userProfile.subscription.promotionType = promotionType;
			}
		}
		else if (!paidSub && freeSub) {
			status = 'free';
		}

		if (paidSub) {
			return {
				...userProfile,
				entitlements: paidSub ? paidSub.entitlements : [],
				customerSegment: paidSub ? paidSub.customerSegment : null,
			};
		}

		// user has no paid sub
		userProfile = {
			...userProfile,
			subscription: {
				...freeSub,
				promotionType: 'none',
				stakeholder: false,
				status,
				storageGB: 'unknown',
				termStartDate: 'unknown',
				customerSegment: 'unknown',
			},
		};

		return {
			...userProfile,
			entitlements: freeSub ? freeSub.entitlements : [],
		};
	}

	_setDefaultProfile() {
		// set default profile in case of error or timeout
		ProfileStorage.writeGqlProfilesResponseToSessionStorage(this._rawProfile);
		ProfileStorage.writeProfilesResponseToSessionStorage(this._profile);

		this._dispatchProfileFetchError();
		this._dispatchProfileFetchCompleted();
	}

	_performProfileFetch(body: Record<string, unknown>): Promise<any> {
		const timeout = setTimeout(() => {
			window.cchome.util.logger.error('Profile Service', 'ProfileService fetch timed out');
			if (!this._profile) {
				this._profile = emptyProfile;
			}

			if (!this._rawProfile) {
				this._rawProfile = emptyGqlProfile;
			}

			this._setDefaultProfile();
			clearTimeout(timeout);
		}, 5000);

		return new Promise((resolve, reject) => {
			const postBody = JSON.stringify(body);

			const obj = {
				hostname: getConfig('GRAPHQL_LINK'),
				path: '/graphql',
				method: 'POST',
				headers: {
					Authorization: `Bearer ${adobeIMS.getAccessToken()}`,
					'x-api-key': 'CCHomeWeb1',
					'x-adobe-app-id': 'CCHomeWeb1',
					'x-request-id': uuid.v4(),
					'Accept-Language': 'en',
					'Content-Type': 'application/json',
				},
				dataType: 'json',
				withCredentials: false,
			};

			const request = https.request(obj, (res) => {
				clearTimeout(timeout);
				// Report status to the profile store
				if (res.statusCode && res.statusCode >= 300) {
					window.cchome.util.logger.error('Profile Service', 'Problem with ProfileService request', res);
					const profileServiceError = new Error('ProfileService error');
					reject(profileServiceError);
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					// https://jira.corp.adobe.com/browse/CCH-4427
					res.setEncoding('utf8');
					res.on('data', chunk => (data += chunk));
					res.on('end', () => {
						// Success!
						try {
							if (
								window.cchome.util.hasFeature('test_injectUserData')
								&& sessionStorage
								// $FlowFixMe
								&& sessionStorage.getItem('cchome_raw_profile')
								// $FlowFixMe
								&& sessionStorage.getItem('ORIGINAL_PROFILE')
								// $FlowFixMe
								&& JSON.stringify(JSON.parse(data).data.account)
									=== JSON.stringify(
										JSON.parse(sessionStorage.getItem('ORIGINAL_PROFILE') || '{}').account,
									)
							) {
								// intercepting the data coming back from server and replacing it with the previous cchome_raw_profile (which could be altered by the profile console)
								// also checking if the data coming back is exactly the same as the ORIGINAL_PROFILE, if they are different that means profile data from server has
								// new data and we should still use the server data
								// $FlowFixMe
								resolve({data: JSON.parse(sessionStorage.getItem('cchome_raw_profile') || '{}')});
							}
							else {
								resolve(JSON.parse(data));
							}
						}
						catch (e: any) {
							window.cchome.util.logger.error(
								'Profile Service',
								'Problem with parsing ProfileService response',
								e.toString(),
							);
							const profileServiceError = new Error('ProfileService parse error');
							reject(profileServiceError);
						}
					});
				}
			});

			request.on('error', (err) => {
				clearTimeout(timeout);
				// There was a connection error of some sort
				window.cchome.util.logger.error(
					'Profile Service',
					'Problem with ProfileService request',
					err.toString(),
				);
				const profileServiceError = new Error('ProfileService error');
				reject(profileServiceError);
			});

			request.write(postBody);
			request.end();
		});
	}

	/**
	 * fetch user profile data from our graphQL endpoint using raw http requests
	 */
	_fetchProfileFromGraphQL() {
		return this._performProfileFetch({
			query:
				'query {\n   currentTimestamp\n	account {\n       stakeholder\n		subscriptions {\n           free {\n               planName\n               planCode\n               locale\n               entitlements\n           }\n           paid {\n        marketSegments\n       promotionType\n			planName\n               planCode\n               termStartDate\n      termEndDate\n         customerSegment\n               offerType\n               contractStatus\n               locale\n               entitlements\n               changePlanAvailable\n           }\n           \n       }\n       storage {\n           total\n           available\n           used\n       }\n   }\n}',
			variables: {},
		})
			.then((response) => {
				const errors = this._validateProfile(response.data);
				const preFormattedData = this._preFormatData(response.data, errors);
				const formattedResponse = this._formatProfileData(preFormattedData);
				this._profile = formattedResponse;
				this._rawProfile = preFormattedData;

				ProfileStorage.writeGqlProfilesResponseToSessionStorage(this._rawProfile);
				ProfileStorage.writeProfilesResponseToSessionStorage(this._profile);

				this._dispatchSetGqlProfile();
				this._dispatchSetProfile();
				this._dispatchProfileFetchCompleted();

				return this._rawProfile;
			})
			.catch(() => {
				if (!this._profile) {
					this._profile = emptyProfile;
				}

				if (!this._rawProfile) {
					this._rawProfile = emptyGqlProfile;
				}

				this._setDefaultProfile();

				return this._rawProfile;
			});
	}
}

export default ProfileService;
